import { useEffect, useState } from "react";
import { api } from "../api";
import type { ShortLink } from "../api/short-links";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { EmptyState } from "./ui/empty-state";
import { ToastContainer } from "./ui/toast";
import { CreateShortLinkModal } from "./CreateShortLinkModal";
import { QRCodeModal } from "./QRCodeModal";
import { QRCodeSVG } from "qrcode.react";
import { Plus, ExternalLink, Trash2, Copy, Search, Link2, QrCode, Download } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

interface ShortLinksTabProps {
  showCreateForm?: boolean;
  onCreateFormClose?: () => void;
  shortLinks: ShortLink[];
  onShortLinksChange: (links: ShortLink[]) => void;
}

export function ShortLinksTab({ showCreateForm: externalShowCreateForm = false, onCreateFormClose, shortLinks, onShortLinksChange }: ShortLinksTabProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [qrModal, setQrModal] = useState<{ isOpen: boolean; url: string; title: string }>({ isOpen: false, url: '', title: '' });
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: "success" | "error" | "info" }>>([]);
  const { t } = useLanguage();

  const addToast = (message: string, type: "success" | "error" | "info" = "info") => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Sync external showCreateForm with internal state
  useEffect(() => {
    setShowCreateForm(externalShowCreateForm);
  }, [externalShowCreateForm]);

  const handleCloseForm = () => {
    setShowCreateForm(false);
    onCreateFormClose?.(); // Call external close handler
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir este link curto?")) return; // This confirm message could also be translated if needed, but sticking to plan

    const res = await api.deleteShortLink(id);
    if (!res.ok) {
      addToast(t.dashboard.shortLinks.toast.deleteError + res.error, "error");
    } else {
      onShortLinksChange(shortLinks.filter(link => link.id !== id));
      addToast(t.dashboard.shortLinks.toast.deleteSuccess, "success");
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        addToast(t.dashboard.shortLinks.toast.copySuccess, "success");
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast(t.dashboard.shortLinks.toast.copySuccess, "success");
      }
    } catch (err) {
      addToast(t.dashboard.shortLinks.toast.copyError, "error");
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredLinks =
    normalizedSearch.length > 0
      ? shortLinks.filter(link =>
        `${link.title ?? ""} ${link.target_url} ${link.suffix}`
          .toLowerCase()
          .includes(normalizedSearch)
      )
      : shortLinks;

  const handleCreateSuccess = (newLink: ShortLink) => {
    onShortLinksChange([newLink, ...shortLinks]);
    addToast(t.dashboard.shortLinks.toast.createSuccess, "success");
  };

  return (
    <div className="space-y-6">

      {shortLinks.length > 0 && (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-slate-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t.dashboard.shortLinks.searchPlaceholder}
            className="block w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary sm:text-sm transition-all shadow-sm hover:shadow-md"
          />
        </div>
      )}

      {filteredLinks.length > 0 ? (
        <div className="grid gap-4">
          {filteredLinks.map(link => (
            <Card key={link.id} className="p-4 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">
                      {link.title || t.dashboard.shortLinks.card.untitled}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0DAFFD]/10 text-[#0DAFFD] self-start sm:self-auto">
                      {link.clicks} {t.dashboard.shortLinks.card.clicks}
                    </span>
                  </div>
                  <Button
                    variant="destructive"
                    size="micro"
                    onClick={() => handleDelete(link.id)}
                  >
                    {t.dashboard.shortLinks.card.delete}
                  </Button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-600 truncate mb-2">
                    {link.target_url}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono break-all sm:break-normal">
                      {api.getShortLinkUrl(link.suffix)}
                    </code>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="micro"
                        variant="ghost"
                        onClick={() => copyToClipboard(api.getShortLinkUrl(link.suffix))}
                        icon={<Copy className="w-4 h-4" />}
                        className="flex-1 sm:flex-none"
                      >
                        {t.dashboard.shortLinks.card.copy}
                      </Button>
                      <Button
                        size="micro"
                        variant="ghost"
                        onClick={() => window.open(link.target_url, '_blank')}
                        icon={<ExternalLink className="w-4 h-4" />}
                        className="flex-1 sm:flex-none"
                      >
                        {t.dashboard.shortLinks.card.open}
                      </Button>
                      <Button
                        size="micro"
                        variant="ghost"
                        onClick={() => setQrModal({ isOpen: true, url: api.getShortLinkUrl(link.suffix), title: link.title || t.dashboard.shortLinks.card.untitled })}
                        icon={<QrCode className="w-4 h-4" />}
                        className="flex-1 sm:flex-none"
                      >
                        {t.dashboard.shortLinks.card.viewQr}
                      </Button>
                      <Button
                        size="micro"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            const QRCode = await import('qrcode');
                            const qrDataURL = await QRCode.toDataURL(api.getShortLinkUrl(link.suffix), {
                              scale: 10, // This will create a 500x500 image (50x50 QR code * 10 scale)
                              margin: 2,
                              color: {
                                dark: '#000000',
                                light: '#FFFFFF'
                              }
                            });

                            const downloadLink = document.createElement('a');
                            downloadLink.download = `${link.title || 'link'}-qr-code.png`;
                            downloadLink.href = qrDataURL;
                            downloadLink.click();
                          } catch (error) {
                            console.error('Error generating QR code:', error);
                            addToast(t.dashboard.shortLinks.toast.qrError, 'error');
                          }
                        }}
                        icon={<Download className="w-4 h-4" />}
                        className="flex-1 sm:flex-none"
                      >
                        {t.dashboard.shortLinks.card.downloadQr}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : shortLinks.length > 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">{t.dashboard.shortLinks.noResultsTitle}</h3>
          <p className="text-slate-600 mt-2">
            {t.dashboard.shortLinks.noResultsDesc.replace("{term}", searchTerm)}
          </p>
        </div>
      ) : (
        <EmptyState
          icon={
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#0DAFFD]/10 to-[#0FD8FE]/10 rounded-3xl flex items-center justify-center shadow-inner">
              <Link2 className="w-10 h-10 text-[#0DAFFD]" />
            </div>
          }
          title={t.dashboard.shortLinks.emptyTitle}
          description={t.dashboard.shortLinks.emptyDesc}
          action={
            <Button
              variant="primary"
              onClick={() => setShowCreateForm(true)}
              icon={<Plus className="w-5 h-5" />}
            >
              {t.dashboard.shortLinks.createCta}
            </Button>
          }
        />
      )}

      <CreateShortLinkModal
        open={showCreateForm}
        onClose={handleCloseForm}
        onSuccess={handleCreateSuccess}
      />

      <QRCodeModal
        isOpen={qrModal.isOpen}
        onClose={() => setQrModal({ isOpen: false, url: '', title: '' })}
        url={qrModal.url}
        title={qrModal.title}
      />

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
